import * as React from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";

const STATUS_META = {
  Cleared: { color: "success", Icon: CheckCircleOutlineIcon },
  Suspicious: { color: "warning", Icon: HelpOutlineIcon },
  Fraud: { color: "error", Icon: FlagOutlinedIcon },
};

const statusColor = (status) => STATUS_META[status]?.color || "default";

function StatusChip({ status }) {
  const meta = STATUS_META[status];
  return (
    <Chip
      size="small"
      variant="outlined"
      color={meta ? meta.color : "default"}
      icon={meta ? <meta.Icon fontSize="small" /> : undefined}
      label={status || "—"}
    />
  );
}

StatusChip.propTypes = {
  status: PropTypes.string,
};

function Detail({ label, value }) {
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value || "—"}</Typography>
    </Box>
  );
}

Detail.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function Row({ row, onOpen }) {
  const comment = row.comment || "";
  const preview = comment.length > 60 ? `${comment.slice(0, 60)}…` : comment;

  return (
    <TableRow
      hover
      onClick={() => onOpen(row)}
      sx={{ cursor: "pointer", "&:last-child td": { borderBottom: 0 } }}
    >
      <TableCell>{row.transactionId}</TableCell>
      <TableCell>{row.date}</TableCell>
      <TableCell>{row.total}</TableCell>
      <TableCell>{row.store}</TableCell>
      <TableCell sx={{ maxWidth: 240 }}>
        {preview || (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <StatusChip status={row.status} />
      </TableCell>
      <TableCell align="right">
        <Tooltip title="Review">
          <IconButton
            size="small"
            aria-label={`review transaction ${row.transactionId}`}
            onClick={(event) => {
              event.stopPropagation();
              onOpen(row);
            }}
          >
            <RateReviewOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

Row.propTypes = {
  row: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired,
};

function TransactionDrawer({ open, row, statuses, saving, onClose, onSave }) {
  const [comment, setComment] = React.useState(row ? row.comment || "" : "");

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: "100vw", sm: 480 }, p: 3 }} role="presentation">
        {row && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="h6">
                  Transaction #{row.transactionId}
                </Typography>
                <StatusChip status={row.status} />
              </Box>
              <IconButton aria-label="close" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Stack spacing={1} sx={{ mb: 3 }}>
              <Detail label="Date" value={row.date} />
              <Detail label="Time" value={row.time} />
              <Detail label="Store" value={row.store} />
              <Detail label="Cashier" value={row.cashierName} />
              <Detail label="Contact" value={row.phone} />
              <Detail label="Flagged" value={row.reason} />
              <Detail label="Total" value={row.total} />
            </Stack>

            <Typography variant="subtitle1" gutterBottom>
              Items
            </Typography>
            <Table size="small" aria-label="transaction items">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Id</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(row.details || []).map((detail, index) => (
                  <TableRow key={`${detail.itemId}-${index}`}>
                    <TableCell>{detail.itemName}</TableCell>
                    <TableCell>{detail.itemId}</TableCell>
                    <TableCell align="right">{detail.itemPrice}</TableCell>
                    <TableCell>{detail.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <TextField
              label="Comment"
              multiline
              minRows={3}
              fullWidth
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              sx={{ mt: 3 }}
            />

            <Stack spacing={1} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                disabled={saving}
                startIcon={
                  saving ? <CircularProgress size={16} color="inherit" /> : undefined
                }
                onClick={() => onSave({ status: row.status, comment })}
              >
                Save comment
              </Button>
              <Divider sx={{ my: 1 }}>Change status to</Divider>
              {statuses.map((statusOption) => {
                const meta = STATUS_META[statusOption];
                const enabled =
                  Boolean(
                    row.statusOptions?.[
                      `${statusOption.toLowerCase()}Enabled`
                    ]
                  ) && statusOption !== row.status;
                return (
                  <Button
                    key={statusOption}
                    variant="outlined"
                    color={statusColor(statusOption)}
                    disabled={!enabled || saving}
                    startIcon={meta ? <meta.Icon /> : undefined}
                    onClick={() => onSave({ status: statusOption, comment })}
                  >
                    {statusOption}
                  </Button>
                );
              })}
            </Stack>
          </>
        )}
      </Box>
    </Drawer>
  );
}

TransactionDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  row: PropTypes.object,
  statuses: PropTypes.arrayOf(PropTypes.string).isRequired,
  saving: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default function CollapsibleTable({ rows, statuses, onUpdate }) {
  const [selectedId, setSelectedId] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success",
    undo: null,
  });

  const selectedRow =
    rows.find((row) => row.transactionId === selectedId) || null;

  const closeSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const revert = React.useCallback(
    async (row, previous) => {
      setSaving(true);
      try {
        await onUpdate(row, previous);
        setSnackbar({
          open: true,
          message: "Change reverted",
          severity: "info",
          undo: null,
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Could not undo the change.",
          severity: "error",
          undo: null,
        });
      } finally {
        setSaving(false);
      }
    },
    [onUpdate]
  );

  const handleSave = async (row, edits) => {
    const previous = { status: row.status, comment: row.comment };
    setSaving(true);
    try {
      await onUpdate(row, edits);
      const statusChanged = Boolean(edits.status) && edits.status !== row.status;
      setSnackbar({
        open: true,
        message: statusChanged ? `Status changed to ${edits.status}` : "Comment saved",
        severity: "success",
        undo: () => revert(row, previous),
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Could not save changes. Please try again.",
        severity: "error",
        undo: null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <TableContainer component={Paper}>
        <Table aria-label="transactions table">
          <TableHead>
            <TableRow>
              <TableCell>Transaction Id</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Store</TableCell>
              <TableCell>Comment</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <Row
                key={row.transactionId}
                row={row}
                onOpen={(current) => setSelectedId(current.transactionId)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TransactionDrawer
        key={selectedId}
        open={selectedRow !== null}
        row={selectedRow}
        statuses={statuses}
        saving={saving}
        onClose={() => setSelectedId(null)}
        onSave={(edits) => handleSave(selectedRow, edits)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={closeSnackbar}
          action={
            snackbar.undo ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  const undo = snackbar.undo;
                  setSnackbar((prev) => ({ ...prev, open: false }));
                  undo();
                }}
              >
                Undo
              </Button>
            ) : undefined
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}

CollapsibleTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  statuses: PropTypes.arrayOf(PropTypes.string).isRequired,
  onUpdate: PropTypes.func.isRequired,
};
